import React from "react";
import { useDAWStore, FX_DEFS } from "../../store/dawStore";
import engine from "../../audio/AudioEngine";
import Fader from "./Fader";
import Knob from "./Knob";
import { TrackMeter } from "./Visualizers";
import { X, Plus } from "@phosphor-icons/react";

export default function Mixer() {
  const { tracks, masterVolume, setMasterVolume, updateTrack, setTrackPan, effects, setEffect, toggleMute, toggleSolo,
          masterFxChain, addMasterFx, removeMasterFx, setActivePlugin, setActiveTab, selectTrack } = useDAWStore();

  return (
    <div className="h-full flex flex-col" data-testid="mixer">
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#222]">
        <h3 className="font-display font-bold text-sm">Mixer · Master Bus</h3>
        <span className="label-mono">{tracks.length} channels</span>
      </div>

      <div className="flex-1 flex gap-4 p-6 overflow-auto">
        {tracks.map((t) => (
          <TrackStrip key={t.id} track={t} updateTrack={updateTrack} setTrackPan={setTrackPan} toggleMute={toggleMute} toggleSolo={toggleSolo} />
        ))}

        {tracks.length === 0 && (
          <div className="flex-1 flex items-center justify-center text-white/40 text-sm">
            No tracks. Add tracks from the arranger.
          </div>
        )}

        <div className="w-px bg-[#262626] mx-2" />

        {/* Master strip */}
        <div
          className="flex flex-col items-center gap-3 px-3 py-3 rounded-lg border border-[#3a3a3a] bg-gradient-to-b from-[#1f1f1f] to-[#141414]"
          style={{ minWidth: 130 }}
          data-testid="mixer-master-strip"
        >
          <div className="text-xs font-bold tracking-wider text-[#00FF41]">MASTER</div>
          <div className="flex items-end gap-2">
            <Fader
              value={masterVolume} min={0} max={1} height={180}
              onChange={(v) => { setMasterVolume(v); engine.setMasterVolume(v); }}
              data-testid="master-fader"
            />
            {/* Master green meter (lives next to the fader) */}
            <MasterMeterBar height={180} />
          </div>
          <div className="label-mono mt-1">{Math.round(masterVolume * 100)}%</div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <Knob value={effects.eq.low} min={-12} max={12} step={0.5} size={42} label="LOW" accentColor="#ef4444"
              onChange={(v) => { setEffect("eq", "low", v); engine.setEffect("eq", "low", v); }}
              format={(v) => `${v.toFixed(1)}dB`} data-testid="eq-low-knob" />
            <Knob value={effects.eq.mid} min={-12} max={12} step={0.5} size={42} label="MID" accentColor="#22c55e"
              onChange={(v) => { setEffect("eq", "mid", v); engine.setEffect("eq", "mid", v); }}
              format={(v) => `${v.toFixed(1)}dB`} data-testid="eq-mid-knob" />
            <Knob value={effects.eq.high} min={-12} max={12} step={0.5} size={42} label="HIGH" accentColor="#3b82f6"
              onChange={(v) => { setEffect("eq", "high", v); engine.setEffect("eq", "high", v); }}
              format={(v) => `${v.toFixed(1)}dB`} data-testid="eq-high-knob" />
            <Knob value={effects.reverb.wet} min={0} max={1} step={0.01} size={42} label="VERB" accentColor="#D2691E"
              onChange={(v) => { setEffect("reverb", "wet", v); engine.setEffect("reverb", "wet", v); }}
              format={(v) => `${Math.round(v * 100)}%`} data-testid="reverb-wet-knob" />
          </div>

          {/* v19.13 — Master FX Chain slots. Producers can drop an
              Upward Clipper, EQ, or any FX onto the master bus here.
              Click a slot to open its editor in the plugin view. */}
          <div className="mt-3 w-full">
            <div className="text-[9px] font-mono tracking-wider text-white/40 mb-1">MASTER FX</div>
            <div className="space-y-1" data-testid="master-fx-chain">
              {(masterFxChain || []).map((fx) => {
                const def = FX_DEFS[fx.plugin] || {};
                return (
                  <div
                    key={fx.id}
                    className="flex items-center gap-1 px-2 py-1 rounded border border-[#2a2a2a] bg-[#111] text-[10px] cursor-pointer hover:border-[#3a3a3a]"
                    style={{ borderLeft: `3px solid ${def.color || "#666"}` }}
                    data-testid={`master-fx-${fx.plugin}`}
                    onClick={() => {
                      selectTrack(null);
                      setActivePlugin(`master:${fx.id}`);
                      setActiveTab("plugin");
                    }}
                  >
                    <span className="flex-1 truncate">{def.label || fx.plugin}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeMasterFx(fx.id); }}
                      className="w-4 h-4 rounded hover:bg-[#2a2a2a] text-white/40 hover:text-white flex items-center justify-center"
                      data-testid={`master-fx-remove-${fx.plugin}`}
                    >
                      <X size={8} weight="bold" />
                    </button>
                  </div>
                );
              })}
              <MasterFxAddPicker onPick={addMasterFx} disabled={(masterFxChain || []).length >= 5} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrackStrip({ track, updateTrack, setTrackPan, toggleMute, toggleSolo }) {
  const pan = track.pan ?? 0;
  const panLabel = Math.abs(pan) < 0.02 ? "C" : (pan < 0 ? `L${Math.round(Math.abs(pan) * 100)}` : `R${Math.round(pan * 100)}`);
  return (
    <div
      className="flex flex-col items-center gap-2 px-3 py-3 rounded-lg border bg-gradient-to-b from-[#1c1c1c] to-[#141414]"
      style={{ minWidth: 100, borderColor: track.color + "40" }}
      data-testid={`mixer-strip-${track.id}`}
    >
      <div className="text-xs font-semibold text-center truncate w-full" style={{ color: track.color }}>{track.name}</div>
      <div className="flex gap-1">
        <button
          onClick={() => { toggleMute(track.id); engine.setTrackMuted(track.id, !track.muted); }}
          className={`btn-3d w-7 h-7 text-[10px] font-bold ${track.muted ? "active" : ""}`}
          data-testid={`mixer-mute-${track.id}`}
        >M</button>
        <button
          onClick={() => toggleSolo(track.id)}
          className={`btn-3d w-7 h-7 text-[10px] font-bold ${track.solo ? "active" : ""}`}
          style={track.solo ? { color: "#EAB308" } : {}}
          data-testid={`mixer-solo-${track.id}`}
        >S</button>
      </div>
      {/* Pan knob — Mixer-only. Center = mono, left/right shifts the whole
          track's stereo image. Double-click the knob to recentre to 0. */}
      <div
        className="flex flex-col items-center gap-0.5"
        onDoubleClick={() => { setTrackPan(track.id, 0); engine.setTrackPan(track.id, 0); }}
        title="Double-click to centre"
      >
        <Knob
          value={pan}
          min={-1}
          max={1}
          step={0.01}
          size={34}
          label="PAN"
          accentColor={track.color}
          onChange={(v) => { setTrackPan(track.id, v); engine.setTrackPan(track.id, v); }}
          format={() => panLabel}
          data-testid={`mixer-pan-${track.id}`}
        />
      </div>
      {/* Fader + animated green meter side by side */}
      <div className="flex items-end gap-2">
        <Fader
          value={track.volume ?? 0.8}
          min={0} max={1} height={160}
          onChange={(v) => {
            updateTrack(track.id, { volume: v });
            // Push directly to the audio graph for zero-latency response —
            // setupSequencer reactivity catches up afterwards.
            engine.setTrackVolume(track.id, v);
          }}
          data-testid={`mixer-fader-${track.id}`}
        />
        <TrackMeter trackId={track.id} height={160} width={10} />
      </div>
      <div className="label-mono">{Math.round((track.volume ?? 0.8) * 100)}%</div>
    </div>
  );
}

// v19.13 — Compact "add FX" picker for the Master strip. Renders a
// small dropdown of every FX_DEFS plugin so producers can drop them
// onto the master bus without leaving the mixer.
function MasterFxAddPicker({ onPick, disabled }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="relative">
      <button
        className="w-full h-6 rounded border border-dashed border-[#333] text-[9px] font-mono tracking-wider text-white/40 hover:text-white hover:border-white/40 flex items-center justify-center gap-1 disabled:opacity-30"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        data-testid="master-fx-add"
      >
        <Plus size={8} weight="bold" /> ADD FX
      </button>
      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-50 rounded border border-[#2a2a2a] bg-[#111] shadow-xl overflow-hidden"
          onMouseLeave={() => setOpen(false)}
        >
          {Object.entries(FX_DEFS).map(([key, def]) => (
            <button
              key={key}
              className="w-full text-left px-2 py-1 text-[10px] hover:bg-[#1a1a1a] flex items-center gap-2"
              style={{ borderLeft: `3px solid ${def.color || "#666"}` }}
              onClick={() => { onPick(key); setOpen(false); }}
              data-testid={`master-fx-pick-${key}`}
            >
              {def.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Master output meter (same style as the per-track meter, just driven by the
// master meter on the engine).
function MasterMeterBar({ height = 180 }) {
  const canvasRef = React.useRef(null);
  const peakRef = React.useRef({ value: 0, decay: 0 });
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    const draw = () => {
      const w = canvas.width = canvas.clientWidth;
      const h = canvas.height = canvas.clientHeight;
      ctx.fillStyle = "#050505";
      ctx.fillRect(0, 0, w, h);
      const db = engine.getMasterLevelDb?.();
      const n = (!Number.isFinite(db)) ? 0 : Math.max(0, Math.min(1, (db + 60) / 66));
      const peak = peakRef.current;
      if (n >= peak.value) { peak.value = n; peak.decay = 0; }
      else { peak.decay += 1; if (peak.decay > 30) peak.value = Math.max(n, peak.value - 0.005); }
      const barH = n * h;
      const grad = ctx.createLinearGradient(0, h, 0, 0);
      grad.addColorStop(0,    "#10b981");
      grad.addColorStop(0.7,  "#22c55e");
      grad.addColorStop(0.85, "#eab308");
      grad.addColorStop(1,    "#ef4444");
      ctx.fillStyle = grad;
      ctx.fillRect(0, h - barH, w, barH);
      if (peak.value > 0.02) {
        const py = h - peak.value * h;
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, py - 1, w, 1.5);
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <div
      className="rounded-sm overflow-hidden border border-[#1a1a1a]"
      style={{ width: 12, height, background: "#050505" }}
      data-testid="master-meter"
    >
      <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
    </div>
  );
}
